import { useCallback, useEffect, useState } from "react";
import { firstPosOfAiTask, findAiTaskRanges, useEditor } from "novel";
import { AiTaskInfo } from "../type";
import { DotsLoading } from "@/components/loading";
import { Button } from "./button";
import { CheckIcon, RefreshCcwIcon, XIcon } from "lucide-react";
import { useTiptapContext } from "../tiptap-provider";
import { message } from "antd";
import {
  applyMarkdownInRange,
  clearAiMarksForTask,
} from "@/lib/applyMarkdownInEditor";

type TaskFloatingMenuProps = {
  task: AiTaskInfo;
};

const TaskFloatingMenu = ({ task }: TaskFloatingMenuProps) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const { editor } = useEditor();
  const { removeAiTask, updateAiTaskStatus } = useTiptapContext((s) => s);

  const getManuscriptContainer = useCallback((element: HTMLElement) => {
    if (element.classList.contains("tt-editor__manuscript")) return element;
    if (!element.parentElement) return null;
    return getManuscriptContainer(element.parentElement);
  }, []);

  const calcPos = useCallback(() => {
    if (!editor?.view) return;
    const container = getManuscriptContainer(editor.view.dom as HTMLElement);
    if (!container) return setPos(null);
    const from = firstPosOfAiTask(editor, task.id);
    if (from == null) return setPos(null);
    const nodeDOM = editor.view.nodeDOM(from - 1) as HTMLElement | null;
    if (!nodeDOM) return setPos(null);

    const containerRect = container.getBoundingClientRect();
    const rect = nodeDOM.getBoundingClientRect();
    const top = rect.top - containerRect.top - 32;
    const left = rect.left - containerRect.left;
    setPos({ top, left });
  }, [editor, task.id, getManuscriptContainer]);

  useEffect(() => {
    calcPos();
    const onScroll = () => calcPos();
    const onResize = () => calcPos();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    const unSub = editor?.on?.("transaction", calcPos);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      unSub && editor?.off("transaction", calcPos);
    };
  }, [calcPos, editor]);

  const handleApply = useCallback(() => {
    if (!editor) return;
    const ranges = findAiTaskRanges(editor, task.id);
    if (!ranges) {
      message.warning("找不到生成内容");
      removeAiTask(task.id);
      return;
    }
    applyMarkdownInRange(editor, ranges.from, ranges.to);
    // 解析成功会换掉带 mark 的节点；失败或残留时一律卸蓝字
    clearAiMarksForTask(editor, task.id);
    removeAiTask(task.id);
    message.success("已应用", 1.5);
  }, [editor, task.id, removeAiTask]);

  const handleDiscard = useCallback(() => {
    if (!editor) return;
    const ranges = findAiTaskRanges(editor, task.id);
    if (ranges) {
      editor
        .chain()
        .focus()
        .setTextSelection({ from: ranges.from, to: ranges.to })
        .deleteSelection()
        .run();
    }
    updateAiTaskStatus(task.id, "discarded");
    removeAiTask(task.id);
    message.info("已弃用该段生成", 1.5);
  }, [editor, task.id, removeAiTask, updateAiTaskStatus]);

  const handleRegenerate = useCallback(() => {
    handleDiscard();
    message.info("已清空，请点生成块「播放」再写一次", 2);
  }, [handleDiscard]);

  if (task.status === "discarded") return null;
  if (!pos) return null;

  const generating = task.status === "idle" || task.status === "generating";
  const ready = task.status === "generated";

  return (
    <div
      className="ai-task-floating-menu absolute z-50 px-0.5 py-0.5 bg-primary rounded flex items-center gap-0.5 pointer-events-auto text-sm"
      style={{ top: pos.top, left: pos.left }}
    >
      {ready && (
        <>
          <Button size="sm" className="gap-2" onClick={handleApply}>
            <CheckIcon className="size-4" />
            <span>应用</span>
          </Button>
          <Button size="sm" className="gap-2" onClick={handleRegenerate} title="清空本段，再到上方点播放">
            <RefreshCcwIcon className="size-4" />
            <span>清空</span>
          </Button>
          <Button size="sm" className="gap-2" onClick={handleDiscard}>
            <XIcon className="size-4" />
            <span>弃用</span>
          </Button>
          <div className="w-px h-7 bg-white opacity-50" />
        </>
      )}
      {generating && <DotsLoading color="#fff" className="mx-2" />}
      <span
        className="text-white mx-2 tracking-wide"
        style={{
          fontFamily: "var(--tt-sans, PingFang SC, system-ui, sans-serif)",
          fontVariantNumeric: "tabular-nums lining-nums",
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {task.wordCount}
        <span className="opacity-90"> 字</span>
      </span>
    </div>
  );
};

export default TaskFloatingMenu;
